import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";

interface MotionTimerProps {
  onMotionDetected: () => void; // Callback triggered when motion is detected
  width?: number;
  height?: number;
}

export const MotionTimer: React.FC<MotionTimerProps> = ({
  onMotionDetected,
  width = 640,
  height = 480,
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [previousFrame, setPreviousFrame] = useState<ImageData | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      detectMotion();
    }, 10); // Check for motion every 100ms

    return () => clearInterval(interval);
  }, [previousFrame]);

  const detectMotion = () => {
    if (!webcamRef.current || !webcamRef.current.video) return;

    const video = webcamRef.current.video;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) return;

    canvas.width = width;
    canvas.height = height;

    // Draw the current video frame onto the canvas
    context.drawImage(video, 0, 0, width, height);

    // Get the current frame's pixel data
    const currentFrame = context.getImageData(0, 0, width, height);

    if (previousFrame) {
      // Compare the current frame with the previous frame
      const motionDetected = compareFrames(previousFrame, currentFrame);

      if (motionDetected) {
        onMotionDetected(); // Trigger the callback
      }
    }

    // Update the previous frame
    setPreviousFrame(currentFrame);
  };

  const compareFrames = (frame1: ImageData, frame2: ImageData): boolean => {
    const threshold = 50; // Sensitivity threshold
    const motionThreshold = 10000; // Minimum number of pixels that must change to detect motion
    let motionPixelCount = 0;

    for (let i = 0; i < frame1.data.length; i += 4) {
      // Compare the RGB values of each pixel
      const rDiff = Math.abs(frame1.data[i] - frame2.data[i]);
      const gDiff = Math.abs(frame1.data[i + 1] - frame2.data[i + 1]);
      const bDiff = Math.abs(frame1.data[i + 2] - frame2.data[i + 2]);

      if (rDiff > threshold || gDiff > threshold || bDiff > threshold) {
        motionPixelCount++;
      }

      // If enough pixels have changed, motion is detected
      if (motionPixelCount > motionThreshold) {
        return true;
      }
    }

    return false;
  };

  return (
    <div>
      <Webcam
        audio={false}
        ref={webcamRef}
        width={width}
        height={height}
        screenshotFormat="image/jpeg"
        videoConstraints={{
          width,
          height,
          facingMode: "environment",
        }}
      />
    </div>
  );
};